/**
 * PassivesSection.js — Section 4: Passive stat bonuses list.
 *
 * Each passive has a stat (from VALID_PASSIVE_STATS) and an amount (number, can be negative).
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import React from 'react';
import { Trash2 } from 'lucide-react';
import { VALID_PASSIVE_STATS } from '../../../../engine/schema';

/**
 * Convert kebab-case stat name to title case for display.
 * e.g. 'all-thresholds' -> 'All Thresholds', 'maxHp' -> 'MaxHp'
 */
function formatStatName(stat) {
  if (stat.includes('-')) {
    return stat.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  // camelCase: insert space before capitals
  return stat.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();
}

export default function PassivesSection({ config, dispatch, errors }) {
  const passives = config.passives || [];

  return (
    <div className="space-y-3">
      {passives.map((passive, i) => (
        <div key={i} className="flex items-center gap-3 p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
          <div className="flex-1 grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-400">Stat</label>
              <select
                value={passive.stat || 'evasion'}
                onChange={e => dispatch({ type: 'UPDATE_PASSIVE', passiveIndex: i, updates: { stat: e.target.value } })}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-100 focus:border-red-500 focus:outline-none"
              >
                {VALID_PASSIVE_STATS.map(s => (
                  <option key={s} value={s}>{formatStatName(s)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-400">Amount</label>
              <input
                type="number"
                value={passive.amount}
                onChange={e => dispatch({ type: 'UPDATE_PASSIVE', passiveIndex: i, updates: { amount: parseInt(e.target.value) || 0 } })}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-100 focus:border-red-500 focus:outline-none"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => dispatch({ type: 'REMOVE_PASSIVE', passiveIndex: i })}
            className="text-gray-500 hover:text-red-400"
            title="Remove passive"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      {passives.length === 0 && (
        <p className="text-xs text-gray-500 text-center py-1">Passives provide permanent stat bonuses while this mechanic is active.</p>
      )}
      <button
        type="button"
        onClick={() => dispatch({ type: 'ADD_PASSIVE' })}
        className="w-full py-2 border border-dashed border-gray-600 rounded text-sm text-gray-400 hover:text-red-400 hover:border-red-700"
      >
        + Add Passive
      </button>
    </div>
  );
}
